import { z } from 'zod';
import { PosSupplierSkusQuerySchema } from './admin-inputs';

/**
 * Central catalogue view of the canonical product↔supplier commercial-code
 * mappings. A row represents one association, never a duplicate product.
 */
export const posSupplierSkuMappingRowSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid(),
  canonical_product_id: z.string().uuid().optional(),
  is_historical: z.boolean().optional(),
  product_name: z.string(),
  product_sku: z.string().nullable(),
  variant_id: z.string().uuid().nullable().optional(),
  variant_sku: z.string().nullable().optional(),
  variant_ean: z.string().nullable().optional(),
  variant_attributes: z.record(z.string(), z.string()).nullable().optional(),
  supplier_id: z.string().uuid(),
  supplier_name: z.string(),
  supplier_sku: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
}).strict();
export type PosSupplierSkuMappingRow = z.infer<typeof posSupplierSkuMappingRowSchema>;

export const posSupplierSkuMappingPaginationSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().positive(),
}).strict();
export type PosSupplierSkuMappingPagination = z.infer<typeof posSupplierSkuMappingPaginationSchema>;

export const posSupplierSkuMappingListResponseSchema = z.object({
  items: z.array(posSupplierSkuMappingRowSchema),
  pagination: posSupplierSkuMappingPaginationSchema,
}).strict();
export type PosSupplierSkuMappingListResponse = z.infer<typeof posSupplierSkuMappingListResponseSchema>;

/** Query for the central “Codici fornitore” dashboard. */
export const PosSupplierSkuMappingListQuerySchema = z.object({
  product_id: z.string().uuid().optional(),
  variant_id: z.string().uuid().optional(),
  target_kind: z.enum(['parent', 'variant']).optional(),
  supplier_id: z.string().uuid().optional(),
  code: z.string().trim().min(1).max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
}).strict();
export type PosSupplierSkuMappingListQuery = z.infer<typeof PosSupplierSkuMappingListQuerySchema>;

/**
 * Preserve the original receipt lookup at the dashboard path while consumers
 * migrate to `/supplier-skus/batch`.  The two response shapes remain
 * unambiguous because only the lookup accepts `product_ids`.
 */
export const PosSupplierSkuDashboardOrBatchQuerySchema = z.union([
  PosSupplierSkusQuerySchema,
  PosSupplierSkuMappingListQuerySchema,
]);
export type PosSupplierSkuDashboardOrBatchQuery = z.infer<typeof PosSupplierSkuDashboardOrBatchQuerySchema>;

export function isPosSupplierSkuBatchQuery(
  query: PosSupplierSkuDashboardOrBatchQuery,
): query is z.infer<typeof PosSupplierSkusQuerySchema> {
  return 'product_ids' in query;
}

/**
 * Optimistic-concurrency token for mapping deletion. Omitting it retains
 * compatibility with the legacy endpoint; new clients must send it.
 */
export const PosProductSupplierSkuDeleteQuerySchema = z.object({
  expected_updated_at: z.string().datetime({ offset: true }).optional(),
}).strict();
export type PosProductSupplierSkuDeleteQuery = z.infer<typeof PosProductSupplierSkuDeleteQuerySchema>;

export const posSupplierSkuTargetSchema = z.object({
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable(),
}).strict();

export type PosSupplierSkuTargetBatchInput = z.infer<typeof PosSupplierSkuTargetBatchInputSchema>;

export type PosSupplierSkuTargetBatchResponse = z.infer<typeof posSupplierSkuTargetBatchResponseSchema>;

export const posSupplierSkuTargetBatchResponseSchema = z.object({
  items: z.array(posSupplierSkuTargetSchema.extend({
    id: z.string().uuid(),
    supplier_sku: z.string(),
  })),
}).strict();

export const PosSupplierSkuTargetBatchInputSchema = z.object({
  supplier_id: z.string().uuid(),
  targets: z.array(posSupplierSkuTargetSchema).min(1).max(500),
}).strict();
